import api from './client';

// ============================================
// AUTH API
// ============================================
export const authAPI = {
  signup: async (username, email, password, fullName) => {
    try {
      const { data } = await api.post('/api/auth/signup', {
        username,
        email,
        password,
        full_name: fullName
      });
      return data;
    } catch (error) {
      throw error;
    }
  },

  login: async (username, password) => {
    try {
      const { data } = await api.post('/api/auth/login', {
        username,
        password
      });
      return data;
    } catch (error) {
      throw error;
    }
  },

  verifyEmail: async (token) => {
    try {
      const { data } = await api.post('/api/auth/verify-email', {
        token
      });
      return data;
    } catch (error) {
      throw error;
    }
  },

  getCurrentUser: async () => {
    try {
      const { data } = await api.get('/api/auth/me');
      return data;
    } catch (error) {
      throw error;
    }
  },

  logout: async () => {
    try {
      await api.post('/api/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
    }
  },

  getStoredUser: () => {
    try {
      const userStr = localStorage.getItem('user');
      return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
      console.error('Error getting stored user:', error);
      return null;
    }
  }
};

// ============================================
// POSTS API
// ============================================
export const postsAPI = {
  getAll: async () => {
    try {
      const response = await api.get('/api/posts');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getPaginated: async ({ page = 1, per_page = 500, sort_by = 'date_range', start_date = null, end_date = null } = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (page) queryParams.append('page', page);
      if (per_page) queryParams.append('per_page', per_page);
      if (sort_by) queryParams.append('sort_by', sort_by);
      if (start_date) queryParams.append('start_date', start_date);
      if (end_date) queryParams.append('end_date', end_date);
      const response = await api.get(`/api/posts/v2?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getById: async (postId) => {
    try {
      const response = await api.get(`/api/posts/${postId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  create: async (postData) => {
    try {
      const response = await api.post('/api/posts', postData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  update: async (postId, postData) => {
    try {
      const response = await api.put(`/api/posts/${postId}`, postData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  delete: async (postId) => {
    try {
      await api.delete(`/api/posts/${postId}`);
    } catch (error) {
      throw error;
    }
  },

  addPlatform: async (postId, platform) => {
    try {
      const response = await api.post(`/api/posts/${postId}/platforms/${platform}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  syncPlatformPosts: async (startDate, endDate, platforms = null) => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('start_date', startDate);
      queryParams.append('end_date', endDate);
      if (platforms && Array.isArray(platforms) && platforms.length > 0) {
        queryParams.append('platforms', platforms.join(','));
      }
      const response = await api.get(`/api/posts/sync-platform-posts?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// ============================================
// STATUS API
// ============================================
export const statusAPI = {
  check: async () => {
    try {
      const response = await api.get('/api/status');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  healthCheck: async () => {
    try {
      const response = await api.get('/');
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// ============================================
// PLATFORMS API
// ============================================
export const platformsAPI = {
  // Get list of available platforms
  getAvailable: async () => {
    try {
      const response = await api.get('/api/platforms');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get user's connected platform accounts
  getConnections: async () => {
    try {
      const response = await api.get('/api/platforms/connections');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Initiate OAuth flow for a platform (returns URL to redirect to)
  initiateOAuth: async (platform) => {
    try {
      const response = await api.get(`/api/platforms/${platform}/auth`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Disconnect a platform account
  disconnect: async (platform, connectionId = null) => {
    try {
      const response = await api.post(`/api/platforms/${platform}/disconnect`, {
        connection_id: connectionId
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Refresh platform token
  refreshToken: async (platform, connectionId = null) => {
    try {
      const response = await api.post(`/api/platforms/${platform}/refresh`, {
        connection_id: connectionId
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Subscribe a Facebook Page to Meta webhook delivery
  subscribeFacebookWebhooks: async (connectionId) => {
    try {
      const response = await api.post('/api/platforms/facebook/webhooks/subscribe', {
        connection_id: connectionId
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Reddit-specific: Get user's subreddits
  getRedditSubreddits: async () => {
    try {
      const response = await api.get('/api/platforms/reddit/subreddits');
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// ============================================
// INBOX API
// ============================================
export const inboxAPI = {
  // Get inbox items
  getItems: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.type) queryParams.append('type', params.type);
      if (params.platform) queryParams.append('platform', params.platform);
      if (params.platforms && Array.isArray(params.platforms) && params.platforms.length > 0) {
        queryParams.append('platforms', params.platforms.join(','));
      }
      if (params.is_read !== undefined) queryParams.append('is_read', params.is_read);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.cursor) queryParams.append('cursor', params.cursor);
      if (params.since) queryParams.append('since', params.since);

      const queryString = queryParams.toString();
      const endpoint = `/api/inbox${queryString ? `?${queryString}` : ''}`;

      const response = await api.get(endpoint);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get single inbox item
  getItem: async (itemId) => {
    try {
      const response = await api.get(`/api/inbox/${itemId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get children (replies) for a comment or mention
  getChildren: async (itemId, params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.limit) queryParams.append('limit', params.limit);
      else queryParams.append('limit', 100); // default: load all children, not just 3
      if (params.cursor) queryParams.append('cursor', params.cursor);

      const queryString = queryParams.toString();
      const endpoint = `/api/inbox/${itemId}/children${queryString ? `?${queryString}` : ''}`;

      const response = await api.get(endpoint);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get messages for a conversation
  getConversationMessages: async (threadId, params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.cursor) queryParams.append('cursor', params.cursor);
      if (params.platform_connection_id) queryParams.append('platform_connection_id', params.platform_connection_id);

      const queryString = queryParams.toString();
      const endpoint = `/api/inbox/conversation/${encodeURIComponent(threadId)}${queryString ? `?${queryString}` : ''}`;

      const response = await api.get(endpoint);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  markConversationRead: async (threadId, connectionId) => {
    const response = await api.post(`/api/inbox/conversation/${encodeURIComponent(threadId)}/read`, null, {
      params: { platform_connection_id: connectionId }
    });
    return response.data;
  },

  archiveConversation: async (threadId, connectionId) => {
    const response = await api.post(`/api/inbox/conversation/${encodeURIComponent(threadId)}/archive`, null, {
      params: { platform_connection_id: connectionId }
    });
    return response.data;
  },

  // Manually sync inbox
  sync: async ({ platforms = null, mode = 'incremental', connection_id = null } = {}) => {
    try {
      const response = await api.post('/api/inbox/sync', {
        platforms: platforms,
        mode: mode,
        connection_id: connection_id,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Mark item as read
  markRead: async (itemId) => {
    try {
      const response = await api.post(`/api/inbox/${itemId}/read`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Mark all inbox items read
  markAllRead: async () => {
    try {
      const response = await api.post('/api/inbox/read-all');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Archive item
  archive: async (itemId) => {
    try {
      const response = await api.post(`/api/inbox/${itemId}/archive`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Reply to item
  reply: async (itemId, content) => {
    try {
      const response = await api.post(`/api/inbox/${itemId}/reply`, {
        content: content
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },


};

// ============================================
// ANALYTICS API
// ============================================
export const analyticsAPI = {
  getOverview: async ({ start = null, end = null, platforms = null } = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (start) queryParams.append('start', start);
      if (end) queryParams.append('end', end);
      if (platforms && Array.isArray(platforms) && platforms.length > 0) {
        queryParams.append('platforms', platforms.join(','));
      }
      const response = await api.get(`/api/analytics/overview?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getTopPosts: async ({ start = null, end = null, platforms = null, limit = 25 } = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (start) queryParams.append('start', start);
      if (end) queryParams.append('end', end);
      if (platforms && Array.isArray(platforms) && platforms.length > 0) {
        queryParams.append('platforms', platforms.join(','));
      }
      if (limit) queryParams.append('limit', limit);
      const response = await api.get(`/api/analytics/top-posts?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// ============================================
// PUBLISHING API
// ============================================
export const publishingAPI = {
  // Publish post to platforms
  publishPost: async (postId, platforms, options = {}) => {
    try {
      const response = await api.post(`/api/posts/${postId}/publish`, {
        platforms: platforms,
        options: options
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get post platform status
  getPostPlatformStatus: async (postId) => {
    try {
      const response = await api.get(`/api/posts/${postId}/platforms`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Delete a published platform post
  deletePlatformPost: async (postId, statusId) => {
    try {
      const response = await api.delete(`/api/posts/${postId}/platforms/${statusId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Edit a published platform post
  editPlatformPost: async (postId, statusId, content) => {
    try {
      const response = await api.put(`/api/posts/${postId}/platforms/${statusId}`, { content });
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// ============================================
// COMMENTS API
// ============================================
export const commentsAPI = {
  // Get comments for a specific post by database Post ID
  getByPostId: async (postId, { nested = false, platform = null, limit = 100, sync = false, cursor = null } = {}) => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('nested', nested ? 'true' : 'false');
      if (platform) queryParams.append('platform', platform);
      if (limit) queryParams.append('limit', limit);
      if (sync) queryParams.append('sync', 'true');
      if (cursor) queryParams.append('cursor', cursor);
      const response = await api.get(`/api/posts/${postId}/comments?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get children (replies) for a comment
  // Delegates to inboxAPI.getChildren which supports cursor-based pagination
  getChildren: async (commentId, params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.cursor) queryParams.append('cursor', params.cursor);
      const queryString = queryParams.toString();
      const endpoint = `/api/inbox/${commentId}/children${queryString ? `?${queryString}` : ''}`;
      const response = await api.get(endpoint);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Reply to a comment
  reply: async (commentId, content) => {
    try {
      const response = await api.post(`/api/comments/${commentId}/reply`, {
        content: content
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get comments for a specific post by platform_post_id (used by CommentsPanel from inbox)
  getByPlatformPostId: async (platformPostId, { platform = null, limit = 100, cursor = null, highlightItemId = null } = {}) => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('platform_post_id', platformPostId);
      if (platform) queryParams.append('platform', platform);
      if (limit) queryParams.append('limit', limit);
      if (cursor) queryParams.append('cursor', cursor);
      if (highlightItemId) queryParams.append('highlight_item_id', highlightItemId);
      const response = await api.get(`/api/inbox/comments-by-platform-post?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Delete a comment
  delete: async (commentId) => {
    try {
      const result = await api.delete(`/api/comments/${commentId}`);
      return result;
    } catch (error) {
      throw error;
    }
  }
};

// ============================================
// SETTINGS API
// ============================================
export const settingsAPI = {
  // Get all sync policies for current user's connections
  getSyncPolicies: async () => {
    try {
      const response = await api.get('/api/settings/sync-policies');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Update sync policy for a specific connection
  updateSyncPolicy: async (connectionId, policyData) => {
    try {
      const response = await api.put(`/api/settings/sync-policy/${connectionId}`, policyData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get usage summary for current billing period
  getUsageSummary: async () => {
    try {
      const response = await api.get('/api/settings/usage-summary');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get paginated usage history
  getUsageHistory: async ({ page = 1, per_page = 50 } = {}) => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('page', page);
      queryParams.append('per_page', per_page);
      const response = await api.get(`/api/settings/usage-history?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get sync status per connection
  getSyncStatus: async () => {
    try {
      const response = await api.get('/api/inbox/sync/status');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get webhook alerts (D5): provider-delivered events we couldn't resolve to
  // an active connection (rotated subscription id, disconnected account).
  getWebhookAlerts: async ({ includeAcknowledged = false } = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (includeAcknowledged) queryParams.append('include_acknowledged', 'true');
      const qs = queryParams.toString();
      const response = await api.get(`/api/settings/webhook-alerts${qs ? `?${qs}` : ''}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Acknowledge a webhook alert so it leaves the open list.
  acknowledgeWebhookAlert: async (alertId) => {
    try {
      const response = await api.post(`/api/settings/webhook-alerts/${alertId}/acknowledge`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// Export everything
const apiExports = {
  auth: authAPI,
  posts: postsAPI,
  status: statusAPI,
  platforms: platformsAPI,
  inbox: inboxAPI,
  publishing: publishingAPI,
  analytics: analyticsAPI,
  comments: commentsAPI,
  settings: settingsAPI,
  healthCheck: statusAPI.healthCheck,
};

export default apiExports;
